<template>
  <div class="container container-sm px-0 screener">
    <div class="sm-col-12">
      <div class="col-12 categories Custom_Scrollbar">
        <screener-button
          v-for="(cat, i) in categoriesButtons"
          :key="i"
          :filled="activeCategory === cat.catName"
          :isNew="cat.isNew"
          @click="changeCategory(cat.catName)"
          >{{ cat.name }}</screener-button
        >
      </div>
      <div class="container pr-0 row">
        <div class="col-8 col-md-auto">
          <horizontal-selector
            :options="screenerModes"
            @select="
              (v) => {
                changeMode(v)
              }
            "
            :active="activeMode"
          />
        </div>
        <div class="col-8 col-md-4">
          <search
            :autoSearchButton="false"
            :autocomplete="false"
            @update="onSearchUpdate"
            @search="onSearchUpdate"
          />
        </div>
        <div class="col-4 col-md-2 px-0">
          <market-selector />
        </div>
      </div>
      <div>
        <div class="col-12">
          <screener-table
            ref="table"
            :rows="finalList"
            :sorts="currentSorts"
            :activeMode="activeMode"
            @visibilityChanged="toggleTickerSubscription"
            @rowclick="followSymbol"
            @changeSort="changeSort"
          />
          <infinite-loading
            ref="infload"
            v-if="canBeLoaded"
            @infinite="loadMore"
            spinner="spiral"
          >
            <div slot="no-more"></div>
            <div slot="error" slot-scope="{ trigger }">
              {{ $t('messages.loadError') }}
              <br />
              <button class="btn-try-infinite" @click="trigger">
                {{ $t('messages.tryAgain') }}
              </button>
            </div>
          </infinite-loading>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import InfiniteLoading from 'vue-infinite-loading'

import filters from '@/utils/filters'
import Search from '@/components/Widgets/NewsSearch'
import ScreenerTable from '@/components/Screener/ScreenerTable'
import Button from '@/components/Screener/Button'
import HorizontalSelector from '@/components/Screener/HorizontalSelector'
import MarketSelector from '@/components/Header/Market/MarketSelector.vue'
import { getMetaSocialObject } from '@/utils/funcs'

// const updateObject = (arr, key, newval) => {
//   var match = _.find(arr, key)
//   if (match) _.merge(match, newval)
//   else arr.push(newval)
// }
const MAXMINPERCENT = 0.05

const defaultFilters = {
  growing: (row) => row.deriviatives.changePercent >= 0,
  falling: (row) => row.deriviatives.changePercent <= 0,
  underestimated: (row) => row.deriviatives.fundDiff >= 0,
  overrated: (row) => row.deriviatives.fundDiff <= 0,
  maximums: (row) => row.deriviatives.position52W >= 1 - MAXMINPERCENT,
  minimums: (row) => row.deriviatives.position52W <= MAXMINPERCENT,

  // positiveNews: (row) =>
  //   row.sentiments.magazines >= 0.5 && row.sentiments.socials >= 0.5,
  // negativeNews: (row) =>
  //   row.sentiments.magazines <= 0.5 && row.sentiments.socials <= 0.5,
}

const defaultSorts = {
  growing: {
    field: 'deriviatives.changePercent',
    direction: 'down',
  },
  falling: {
    field: 'deriviatives.changePercent',
    direction: 'up',
  },
  underestimated: {
    field: 'deriviatives.fundDiff',
    direction: 'down',
  },
  overrated: {
    field: 'deriviatives.fundDiff',
    direction: 'up',
  },
  default: {
    field: 'symbol',
    direction: 'up',
  },
  maximums: {
    field: 'deriviatives.position52W',
    direction: 'down',
  },
  minimums: {
    field: 'deriviatives.position52W',
    direction: 'up',
  },
  // positiveNews: (rowA, rowB) =>
  //   row.sentiments.magazines >= 0.5 && row.sentiments.socials >= 0.5,
  // negativeNews: (rowA, rowB) =>
  //   row.sentiments.magazines <= 0.5 && row.sentiments.socials <= 0.5,
}

const defaultSortObject = {
  field: 'symbol',
  direction: 'up',
}

export default {
  name: 'Screener',
  components: {
    search: Search,
    'screener-table': ScreenerTable,
    InfiniteLoading,
    'screener-button': Button,
    'market-selector': MarketSelector,
    'horizontal-selector': HorizontalSelector,
  },
  data() {
    return {
      searchString: '',
      listApi: [],
      listBO: [],
      pageOffset: 0,
      tickersVisible: [],
      canBeLoaded: false,
      noMoreData: false,
      activeCategory: false,
      activeSort: defaultSortObject,
      filters: defaultFilters,
      sorts: defaultSorts,
      customCategories: {},
      activeMode: 'common',
    }
  },
  methods: {
    onSearchUpdate(str) {
      this.searchString = String(str).toLowerCase()

      this.$refs.table.unsubscribeAll()
      this.loadMore(false, true)
    },
    loadMore($state, all = false) {
      if (this.noMoreData) return
      // no need to call $state funcs because
      // this only called by search
      if (!$state) $state = this.$refs.infload.stateChanger
      const pageSize = 30
      this.$store
        .dispatch('request/getScreenerAPI', {
          market: String(this.$store.state.market.platform),
          pageOffset: this.pageOffset,
          pageSize,
        })
        .then((res) => {
          this.listApi.push(
            ...res
              .filter((el) => _.isNumber(el.currentPrice))
              .map((apiEntry) => ({
                ...apiEntry,
                metrics52W: {
                  o: (apiEntry.metrics52W && apiEntry.metrics52W.open) || 0,
                  h: (apiEntry.metrics52W && apiEntry.metrics52W.high) || 1,
                  l: (apiEntry.metrics52W && apiEntry.metrics52W.low) || 0,
                  c: (apiEntry.metrics52W && apiEntry.metrics52W.close) || 1,
                  v: (apiEntry.metrics52W && apiEntry.metrics52W.volume) || 1,
                },
                metrics3m: {
                  o: (apiEntry.metrics3m && apiEntry.metrics3m.open) || 0,
                  l: (apiEntry.metrics3m && apiEntry.metrics3m.low) || 0,
                  h: (apiEntry.metrics3m && apiEntry.metrics3m.high) || 1,
                  c: (apiEntry.metrics3m && apiEntry.metrics3m.close) || 1,
                  v: (apiEntry.metrics3m && apiEntry.metrics3m.volume) || 1,
                },
              }))
          )
          $state.loaded()

          if (pageSize > res.length) {
            this.noMoreData = true
            $state.complete()
          } else if (all) {
            // if need all data
            this.loadMore(false, true)
          }
        })
        .catch((err) => {
          console.log('loadMore', err)
          $state.error(err)
        })
      this.pageOffset += 1
    },
    toggleTickerSubscription({ ticker, visible }) {
      if (visible) this.tickersVisible.push(ticker)
      else if (this.tickersVisible.indexOf(ticker) > -1)
        this.tickersVisible.splice(this.tickersVisible.indexOf(ticker), 1)

      this.$store.dispatch(
        `request/socket/${visible ? 'subscribe' : 'leave'}`,
        { ticker: ticker, ticksOnly: true }
      )
    },
    prepareSockets() {
      this.$store.commit('market/updateMarket', this.$route.params.market)
      this.$store.commit('request/socket/toggleScreener', true)
      this.unsubscribeMutation = this.$store.subscribe((mutation) => {
        if (mutation.type !== 'request/socket/screenerTick') return

        const { h, l, c } = mutation.payload
        const ticker =
          mutation.payload.ticker.indexOf('/') > -1
            ? mutation.payload.ticker.split('/')[1]
            : mutation.payload.ticker
        const index = _.findIndex(this.listApi, {
          symbol: ticker,
        })
        if (index < 0) {
          console.log('Error while updating prices!', ticker)
          return
        }

        const obj = this.listApi[index]
        obj.directionUp = obj.currentPrice < c
        obj.directionDown = obj.currentPrice > c
        setTimeout(() => {
          obj.directionUp = undefined
          obj.directionDown = undefined
        }, 100)
        obj.currentPrice = c
        const defaultMetrics = {
          o: 0,
          h: 0,
          l: 0,
          c: 0,
        }
        obj.metrics52W = obj.metrics52W
          ? {
              ...obj.metrics52W,
              c: c,
              h: _.max([obj.metrics52W.h, h]),
              l: _.min([obj.metrics52W.l, l]),
            }
          : defaultMetrics
        obj.metrics3m = obj.metrics3m
          ? {
              ...obj.metrics3m,
              c: c,
              h: _.max([obj.metrics3m.h, h]),
              l: _.min([obj.metrics3m.l, l]),
            }
          : defaultMetrics
        // console.log('obj', obj)
        // Replace item at index using native splice
        // this.listApi.splice(index, 1, obj);
      })

      if (!this.$store.state.request.socket.ready) {
        this.$store.dispatch('request/socket/init')
      }
    },
    followSymbol(row) {
      const symbol = String(row.symbol)
      const market = String(row.market)
      // here we try to send all data from row into store
      this.$store.commit('market/updateLastDayPrice', row.lastClose.last)
      this.$store.commit('market/updateCurrentPrice', row.currentPrice)
      this.$store.commit('market/updateTicker', symbol)
      this.$store.commit('market/updateMarket', market)
      try {
        while (this.tickersVisible.length > 0)
          this.toggleTickerSubscription({
            ticker: this.tickersVisible[0],
            visible: false,
          })
      } catch (error) {
        console.log(
          'Error while flushing subscriprions',
          JSON.stringify(this.tickersVisible),
          error
        )
      }

      this.$router.push(`/dashboard/${symbol}/${market}`)
      // this.$store.commit()
      // this.$store.commit()
    },
    getColor(val, border = 0) {
      return val > border ? '#08B64D' : '#ED5051'
    },
    getDeriviatives(row) {
      let change = '-',
        changePercent = '-',
        fundDiff = '-',
        cap = '-',
        meanVol = '-',
        low52 = '-',
        high52 = '-',
        colors = {},
        position52W = 0.5,
        perfDivSum12m = '-',
        perfNearestDiv = '-'

      if (
        _.isNumber(row.currentPrice) &&
        _.isNumber(_.get(row, 'lastClose.last', 'def'))
      ) {
        change = filters.financial(
          row.currentPrice - row.lastClose.last,
          row.digitsAfterDot
        )
        changePercent = Number((change / row.lastClose.last) * 100).toFixed(2)

        colors.change = this.getColor(change)
        colors.changePercent = this.getColor(changePercent)
      }

      if (row.priceUnderReview) {
        row.fundamentalPrice = Number.MIN_SAFE_INTEGER
        fundDiff = Number.MIN_SAFE_INTEGER
      }

      if (
        _.isNumber(row.fundamentalPrice) &&
        row.fundamentalPrice !== Number.MIN_SAFE_INTEGER &&
        _.isNumber(row.currentPrice)
      ) {
        fundDiff = filters.financial(
          (row.fundamentalPrice / row.currentPrice - 1) * 100,
          0
        )
        colors.fundDiff = this.getColor(fundDiff)
      }

      if (
        _.isNumber(row.currentPrice) &&
        _.isNumber(Number(row.marketCapNum)) &&
        _.isNumber(row.adrCoeff)
      )
        cap = row.currentPrice * row.marketCapNum * row.adrCoeff

      if (_.isNumber(_.get(row, 'metrics3m.v', 'def')))
        meanVol = row.metrics3m.v

      if (
        _.isNumber(_.get(row, 'sentiments.socials', 'def')) &&
        _.isNumber(_.get(row, 'sentiments.magazines', 'def'))
      ) {
        colors.socials = this.getColor(row.sentiments.socials, 0.5)
        colors.magazines = this.getColor(row.sentiments.magazines, 0.5)
      }

      if (_.isNumber(_.get(row, 'metrics52W.l', 'def'))) {
        low52 = filters.financial(row.metrics52W.l, row.digitsAfterDot)
      }
      if (_.isNumber(_.get(row, 'metrics52W.h', 'def'))) {
        high52 = filters.financial(row.metrics52W.h, row.digitsAfterDot)
      }

      if (
        _.isNumber(_.get(row, 'metrics52W.l', 'def')) &&
        _.isNumber(_.get(row, 'metrics52W.h', 'def')) &&
        _.isNumber(_.get(row, 'metrics52W.c', 'def'))
      ) {
        position52W =
          (row.metrics52W.c - row.metrics52W.l) /
          (row.metrics52W.h - row.metrics52W.l)
      }

      // dividends
      if (_.isNumber(row.divSum12m) && _.isNumber(row.currentPrice)) {
        perfDivSum12m = filters.financial(
          (row.divSum12m / row.currentPrice) * 100
        )
      }

      if (_.isNumber(_.get(row, 'nearestDiv.dividend', 'def'))) {
        perfNearestDiv = filters.financial(
          (row.nearestDiv.dividend / row.currentPrice) * 100
        )
      }

      return {
        change,
        changePercent,
        fundDiff,
        cap,
        meanVol,
        low52,
        high52,
        position52W,
        colors,
        perfDivSum12m,
        perfNearestDiv,
      }
    },
    changeCategory(to, routePush = true) {
      if (this.activeCategory != to) {
        this.activeCategory = to
        this.activeSort = this.sorts[to]
        routePush &&
          this.$router.push({
            path: this.$route.path,
            query: {
              ...this.$route.query,
              category: to,
            },
          })
      } else {
        this.activeCategory = false
        this.activeSort = this.sorts['default']
        let q = { ...this.$route.query } // to copy object
        if (q.category) delete q.category

        routePush &&
          this.$router.push({
            path: this.$route.path,
            query: q,
          })
      }

      this.$refs.table.unsubscribeAll()
      this.loadMore(false, true)
    },
    changeMode(to, routePush = true) {
      if (this.activeMode != to) {
        this.activeMode = to
        this.activeSort = this.sorts['default']

        routePush &&
          this.$router.push({
            path: this.$route.path,
            query: {
              ...this.$route.query,
              mode: to,
            },
          })
      }
    },
    changeSort(sort) {
      if (this.$route.query.category)
        this.changeCategory(this.$route.query.category)
      this.$refs.table.unsubscribeAll()
      this.loadMore(false, true)

      if (this.activeSort.field === sort) {
        if (this.activeSort.direction === 'up') {
          this.activeSort.direction = 'down'
          return
        }
        if (this.activeSort.direction === 'down') {
          this.activeSort.direction = 'up'
          this.activeSort.field = 'symbol'
          return
        }
      } else {
        this.activeSort.direction = 'up'
        this.activeSort.field = sort
      }
    },
    getCategories() {
      this.$store
        .dispatch('request/getScreenerCategories')
        .then((res) => {
          this.customCategories = _.keyBy(
            res
              .filter(
                (c) =>
                  c.market &&
                  c.market.name ==
                    String(this.$route.params.market).toUpperCase()
              )
              .sort((a, b) => b.id - a.id),
            'slug'
          )

          this.sorts = {
            ..._.mapValues(this.customCategories, (el) => ({
              field: el.sortBy.replace('___', '.'),
              direction: { ASC: 'up', DESC: 'down' }[el.sortDirection],
              name_en: el.name_en,
              name_ru: el.name_ru,
              custom: true,
              isNew: el.isNewCategory,
            })),
            ...defaultSorts,
          }
          this.filters = {
            ..._.mapValues(this.customCategories, (el) => (row) =>
              el.tickers.indexOf(row['name']) > -1
            ),
            ...defaultFilters,
          }
        })
        .then(() => {
          if (this.$route.query && this.$route.query.category)
            this.changeCategory(this.$route.query.category, false)
        })
    },
    initialize() {
      this.listApi = []
      this.listBO = []
      this.canBeLoaded = true
      this.noMoreData = false
      this.pageOffset = 0
      this.activeCategory = false
      this.searchString = ''
      this.activeSort = defaultSortObject
      if (
        this.$route.query &&
        this.$route.query.mode &&
        this.screenerModes.filter(
          (mode) => mode.value == this.$route.query.mode
        ).length > 0 // if there is such mode
      ) {
        this.activeMode = this.$route.query.mode
      }
      this.getCategories()
      this.$store.commit('market/updateMarket', this.$route.params.market)
      if (this.$refs.infload && this.$refs.infload.stateChanger)
        this.$refs.infload.stateChanger.reset()

      this.$store
        .dispatch('layout/updateAll', { dashboard: false })
        .then(() => {
          this.$store.commit(
            'market/updatePlatforms',
            this.$store.state.market.allPlatforms
          )
        })
        .finally(() => {
          if (window.__PRERENDER_INJECTED)
            setTimeout(() => {
              document.dispatchEvent(new Event('prerender-now'))
            }, 10000)
        })

      this.$store
        .dispatch('request/getScreenerBO', {
          market: this.$store.state.market.platform,
        })
        .then((res) => {
          this.listBO = res
        })
    },
  },
  computed: {
    screenerModes() {
      return [
        {
          value: 'common',
          name: this.$t('screener.modes.common'),
        },
        {
          value: 'dividends',
          name: this.$t('screener.modes.dividends'),
        },
      ]
    },
    finalList() {
      const filtered =
        this.activeCategory && this.filters[this.activeCategory]
          ? this.listFiltered.filter(this.filters[this.activeCategory])
          : this.listFiltered
      const sort =
        [
          'companyName',
          'symbol',
          'name',
          'tickerType',
          'currency',
          'nearestDiv.status',
          'nearestDiv.lastDivDay',
          'nearestDiv.period',
        ].indexOf(this.activeSort.field) > -1
          ? (_a, _b) => {
              // if string field
              const a = _.get(_a, this.activeSort.field, '--'),
                b = _.get(_b, this.activeSort.field, '--')
              if (a === null || a === '--') return 1
              if (b === null || b === '--') return -1
              if (a < b) {
                return -1 * (this.activeSort.direction == 'up' ? 1 : -1)
              }
              if (a > b) {
                return 1 * (this.activeSort.direction == 'up' ? 1 : -1)
              }
              return 0
            }
          : (_a, _b) => {
              // if number field
              let a = _.get(_a, this.activeSort.field),
                b = _.get(_b, this.activeSort.field)

              a = a === null ? NaN : Number(a)
              b = b === null ? NaN : Number(b)
              if (isFinite(a - b) && a !== null && b !== null) {
                return (this.activeSort.direction == 'up' ? 1 : -1) * (a - b)
              } else {
                return isFinite(a) ? -1 : 1
              }
            }

      return filtered.sort(sort)
    },
    list() {
      return this.listApi
        .map((row) => {
          const re = new RegExp(':' + row.symbol + '$', 'i')

          const filtered = this.listBO.filter((rowBO) => {
            // console.log(
            //   'test rowBO',
            //   row.symbol,
            //   rowBO.name,
            //   ':' + rowBO.name + '$'
            // )
            // return rowBO.name == `${row.market}:${row.symbol}`
            return re.test(rowBO.name)
          })

          if (Array.from(filtered).length === 1) {
            row = {
              ...filtered[0],
              ...row,
              ...filtered[0].ticker_id,
            }
            // _.assign(row, filtered[0])
          }

          row.searchable = `${String(
            row.symbol
          ).toLowerCase()}${row.companyName_ru &&
            row.companyName_ru.toLowerCase()}${row.companyName_en &&
            row.companyName_en.toLowerCase()}`

          if (row.nearestDiv && row.nearestDiv.lastDivDay === null) {
            row.nearestDiv.lastDivDay = '--'
          }

          row.deriviatives = this.getDeriviatives(row)

          return row
        })
        .filter((row) => _.isString(row.companyName_ru))
    },
    listFiltered() {
      return this.list.filter(
        (row) => String(row.searchable).indexOf(this.searchString) > -1
      )
      // .sort(
      //   (rowA, rowB) =>
      //     String(rowA.searchable).indexOf(this.searchString) -
      //     String(rowB.searchable).indexOf(this.searchString)
      // )
    },
    currentSorts() {
      const sorts = {}
      sorts[this.activeSort.field] = this.activeSort.direction
      return sorts
    },
    categoriesButtons() {
      const categoriesList = _.intersection(
        Object.keys(this.sorts),
        Object.keys(this.filters)
      )

      return [
        ...categoriesList.map((catName) => ({
          name: this.sorts[catName].custom
            ? this.sorts[catName][`name_${this.$i18n.locale}`] ||
              this.sorts[catName]['name_en']
            : this.$t('screener.categories.' + catName),
          catName: catName,
          isNew: this.sorts[catName].isNew || false,
        })),
        // ...this.customCategories.filter(
        //   (cat) =>
        //     cat.market &&
        //     cat.market.name == String(this.$route.params.market).toUpperCase()
        // ).map(cat => ({
        //   name: cat[`name_${this.$i18n.locale}`] || cat.name_en,
        //   catName:
        // })),
      ]
    },
  },
  created() {},
  mounted() {
    this.initialize()
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (vm && to.params.market) vm.prepareSockets()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.unsubscribeMutation && this.unsubscribeMutation()
    this.$refs.table && this.$refs.table.unsubscribeAll()

    this.canBeLoaded = false

    this.$store.commit('request/socket/toggleScreener', false)
    next()
  },
  watch: {
    $route(newVal, oldVal) {
      if (newVal.path == oldVal.path) return
      // unsub all sockets
      this.$refs.table && this.$refs.table.unsubscribeAll()
      // change market
      this.$store.commit('market/updateMarket', this.$route.params.market)
      // load all again
      this.initialize()
    },
  },

  metaInfo() {
    const localeName =
      'screener.titles.' +
      String(this.$store.state.market.platform).toUpperCase()
    const title = `${
      this.$te(localeName)
        ? this.$t(localeName)
        : this.$t('screener.titles.MOEX')
    } ${this.$t('messages.titlePostfix')}`

    const desc = this.$store.state.market.companyDescription
    const url = process.env.VUE_APP_SITE_URL + this.$route.path
    return getMetaSocialObject(
      title,
      desc,
      url,
      this.$store.state.ticker.previewImgUrl || '/img/preview_default_ru.png'
    )
  },
}
</script>

<style lang="scss">
.screener {
  color: black;
  padding-top: 2.125rem;

  div.categories {
    padding-bottom: 0.8rem;
    margin-bottom: 0.7rem;
    overflow-x: auto;
    width: 100%;
    white-space: nowrap;
  }
}
</style>
